import { memo, useState } from "react";
import { BadgeCheck } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useUserBadgesFor } from "@/hooks/useBadges";
import { BADGE_CHECK_COLOR } from "@/lib/badgeColors";
import { cn } from "@/lib/utils";

interface UserNameProps {
  userId: string | null | undefined;
  name: string | null | undefined;
  className?: string;
  fallback?: string;
}

/** A member's name plus a check mark when they hold any active completion badge. */
export const UserName = memo(function UserName({ userId, name, className, fallback = "Unknown" }: UserNameProps) {
  const { badges } = useUserBadgesFor(userId);
  const [open, setOpen] = useState(false);

  return (
    <span className={cn("inline-flex items-center gap-1 min-w-0", className)}>
      <span className="truncate">{name || fallback}</span>
      {badges.length > 0 && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={`Badges: ${badges.map((b) => b.name).join(", ")}`}
              className="inline-flex shrink-0 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setOpen((o) => !o);
              }}
              onMouseEnter={() => setOpen(true)}
              onMouseLeave={() => setOpen(false)}
            >
              <BadgeCheck className="h-4 w-4" style={{ color: BADGE_CHECK_COLOR }} />
            </button>
          </PopoverTrigger>
          <PopoverContent
            className="w-auto max-w-xs p-2 text-xs"
            side="top"
            onClick={(e) => e.stopPropagation()}
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <p className="font-medium mb-1">Badges</p>
            <ul className="space-y-0.5">
              {badges.map((b) => (
                <li key={b.badge_id}>{b.name}</li>
              ))}
            </ul>
          </PopoverContent>
        </Popover>
      )}
    </span>
  );
});
